import { handleApiError } from '../core/apiError';
import axiosInstance from '../core/axiosInstance';

/**
 * Sends a request to the server to create a new building.
 * 
 * This function sends a `POST` request to the `/building/add` endpoint 
 * to create a new building on the server. The 
 * server's response, which includes the list, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the success message.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const createNewRentalProperty = async (rentalPropertyData) => {
  try {
    const response = await axiosInstance.post('/rental_property', rentalPropertyData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};


/**
 * Sends a request to the server to fetch the list of building.
 * 
 * This function sends a `GET` request to the `/building` endpoint 
 * to retrieve the list of buildings from the server. The 
 * server's response, which includes the building list, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the building list.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchRentalPropertyList = async () => {
  try {
    const response = await axiosInstance.get('/rental_property');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch the list of building.
 * 
 * This function sends a `GET` request to the `/building` endpoint 
 * to retrieve the list of buildings from the server. The 
 * server's response, which includes the building list, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the building list.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchAvailableRentalProperties = async (tenantId = null) => {
  try {
    const url = tenantId
      ? `/rental_property/available?tenantId=${tenantId}`
      : `/rental_property/available`;

    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Sends a request to the server to fetch details of specific building.
 * 
 * This function sends a `GET` request to the `/building/${building_id}` endpoint 
 * to retrieve details of specific building from the server. The 
 * server's response, which includes the details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const getRentalPropertyDetailsById = async (property_id) => {
  try {
    const response = await axiosInstance.get(`/rental_property/${property_id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch details of specific building.
 * 
 * This function sends a `PUT` request to the `/building/${building_id}` endpoint 
 * to update details of specific building on the server. The 
 * server's response, which includes the new details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const updateRentalPropertyDetails = async (rental_property_id, payload) => {
  try {
    const response = await axiosInstance.put(`/rental_property/${rental_property_id}`, payload);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to delete details of specific building.
 * 
 * This function sends a `DELETE` request to the `/building/delete/${building_id}` endpoint 
 * to delete details of specific building from the server. The 
 * server's response, which includes the new details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const deleteRentalPropertyById = async (rental_property_id) => {
  try {
    const response = await axiosInstance.delete(`/rental_property/${rental_property_id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};